import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Phone, FileText, ExternalLink, PlayCircle, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon, Overline, Reveal, SectionHeading, Counter } from "../components/common";
import { ResponsiveCarousel } from "../components/ResponsiveCarousel";
import { FlyerDownload } from "../components/FlyerDownload";
import { ExecutiveSnapshot } from "../components/ExecutiveSnapshot";
import OurReach from "../components/OurReach";
import {
  SERVICE_PILLARS,
  SERVICES,
  STATS,
  ABOUT_VALUES,
  IMAGES,
  MOBILE_SCHEDULE,
  CONTACT,
  PUBLICATIONS,
} from "../data/content";

const PillarCard = ({ item }) => (
  <div
    data-testid={`pillar-card-${item.id}`}
    className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col h-full"
  >
    <div className="h-14 w-14 rounded-2xl bg-brand-mist flex items-center justify-center text-brand-navy">
      <Icon name={item.icon} className="h-7 w-7" />
    </div>
    <h3 className="mt-6 text-xl font-semibold font-heading text-brand-navy">
      {item.title}
    </h3>
    <p className="mt-1 text-sm font-semibold uppercase tracking-wider text-brand-teal">
      {item.tagline}
    </p>
    <p className="mt-4 text-slate-500 leading-relaxed">{item.description}</p>
  </div>
);

export default function Home() {
  const { t, i18n } = useTranslation();
  // Language suffix on the flyer PDFs matches the `<suffix>` used elsewhere:
  // English files have no suffix, French files use "-fr".
  const flyerSuffix = i18n.language === "fr" ? "-fr" : "";
  // Collapsible "Download flyer" CTA — tap to reveal Color / B&W options.
  const [flyerOpen, setFlyerOpen] = React.useState(false);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-mist/60 to-white pt-24 sm:pt-28 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05]">
                {t("home.hero.titleLead")}{" "}
                <br className="sm:hidden" />
                <span className="text-brand-teal">{t("home.hero.titleAccent")}</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-500 leading-relaxed max-w-xl text-justify md:text-left">
                {t("home.hero.subtitle")}
              </p>
              <div className="mt-9 flex flex-col items-stretch gap-4 w-full max-w-xs">
                <Link
                  to="/about"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white border-2 border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white px-8 py-3.5 font-semibold transition-colors duration-200 w-full"
                >
                  {t("home.hero.ctaPrimary")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/programs"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-white border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-8 py-3.5 font-semibold transition-colors duration-200 w-full"
                >
                  {t("home.hero.ctaSecondary")}
                </Link>
                {/* Flyer download — primary CTA for partners.
                    Tap once to reveal the two format options (Color / B&W).
                    Language-aware via `flyerSuffix`. */}
                <div className="flex flex-col gap-2 w-full">
                  <button
                    type="button"
                    onClick={() => setFlyerOpen((v) => !v)}
                    aria-expanded={flyerOpen}
                    aria-controls="hero-flyer-options"
                    data-testid="hero-flyer-toggle"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors duration-200 shadow-sm w-full"
                  >
                    <Download className="h-4 w-4" strokeWidth={2.2} />
                    {t("home.hero.flyerLabel")}
                  </button>
                  {flyerOpen && (
                    <div
                      id="hero-flyer-options"
                      data-testid="hero-flyer-options"
                      className="flex gap-2 rounded-2xl border border-brand-mist bg-white/90 backdrop-blur-sm p-2 shadow-sm animate-in fade-in slide-in-from-top-1 duration-200"
                    >
                      <a
                        href={`/downloads/hepcure-flyer-color${flyerSuffix}.pdf`}
                        download={`hepcure-flyer-color${flyerSuffix}.pdf`}
                        data-testid="hero-flyer-color"
                        className="flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-4 py-2 text-sm font-semibold transition-colors"
                      >
                        {t("home.hero.flyerColor")}
                      </a>
                      <a
                        href={`/downloads/hepcure-flyer-bw${flyerSuffix}.pdf`}
                        download={`hepcure-flyer-bw${flyerSuffix}.pdf`}
                        data-testid="hero-flyer-bw"
                        className="flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white px-4 py-2 text-sm font-semibold transition-colors"
                      >
                        {t("home.hero.flyerBW")}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative">
                <div className="absolute -top-6 -right-4 h-40 w-40 rounded-full bg-brand-teal/15 blur-2xl" />
                <div className="absolute -bottom-8 -left-6 h-48 w-48 rounded-full bg-brand-navy/10 blur-2xl" />
                <Link
                  to="/journey"
                  data-testid="home-journey-card"
                  className="group relative block rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-brand-navy/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2"
                >
                  {/* Header — same navy strip pattern as the sharing card for visual continuity */}
                  <div className="bg-brand-navy px-6 py-6 sm:px-8 sm:py-7">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal-light">
                      {t("home.journeyCard.kicker")}
                    </p>
                    <p className="mt-2 text-2xl sm:text-3xl font-heading font-semibold text-white leading-tight">
                      {t("home.journeyCard.titleLead")}
                      <br />
                      <span className="text-brand-cream">{t("home.journeyCard.titleAccent")}</span>
                    </p>
                    <p className="mt-2 text-white/70 leading-relaxed">
                      {t("home.journeyCard.description")}
                    </p>
                  </div>

                  {/* Media — static Scene 1 still (no autoplay, no play button) */}
                  <div className="relative">
                    <img
                      src="/brand/journey-poster.jpg"
                      alt="A HepCURE outreach worker beside our mobile clinic"
                      className="w-full h-[300px] sm:h-[360px] object-cover object-center bg-brand-navy"
                      loading="lazy"
                    />
                    {/* Subtle bottom gradient so the CTA row stays readable */}
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent pointer-events-none" />

                    {/* Bottom CTA row */}
                    <div className="absolute bottom-0 left-0 right-0 px-6 py-4 flex items-center justify-between gap-4">
                      <span className="text-xs font-bold uppercase tracking-[0.18em] text-white/85 drop-shadow">
                        {t("home.journeyCard.audioHint")}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-cream group-hover:text-white transition-colors drop-shadow">
                        {t("home.journeyCard.cta")}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </Link>

                {/* Coming-soon note below the card */}
                <p
                  data-testid="home-journey-coming-soon"
                  className="mt-4 text-center text-xs sm:text-sm font-medium text-slate-500 leading-relaxed px-2 whitespace-pre-line"
                >
                  {t("home.journeyCard.comingSoon")}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DID YOU KNOW */}
      <section
        className="relative overflow-hidden bg-brand-navy py-12 sm:py-16"
        data-testid="did-you-know-section"
      >
        <div className="absolute -top-16 -right-10 h-72 w-72 rounded-full bg-brand-teal/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-brand-teal/10 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-brand-teal-light">
              {t("home.didYouKnow.kicker")}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold font-heading text-white leading-tight">
              {t("home.didYouKnow.title")}
            </h2>
            <p className="mt-5 text-white/75 leading-relaxed text-lg text-justify md:text-left hyphens-auto">
              {t("home.didYouKnow.bodyPart1")}{" "}
              <span className="text-brand-teal-light font-semibold">
                {t("home.didYouKnow.highlight1")}
              </span>{" "}
              {t("home.didYouKnow.bodyPart2")}{" "}
              <br className="hidden lg:block" />
              {t("home.didYouKnow.bodyPart3")}{" "}
              <span className="text-brand-teal-light font-semibold">
                {t("home.didYouKnow.highlight2")}
              </span>{" "}
              {t("home.didYouKnow.bodyPart4")}{" "}
              <span className="text-brand-teal-light font-semibold">
                {t("home.didYouKnow.highlight3")}
              </span>
              {t("home.didYouKnow.bodyPart5")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICE PILLARS */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              overline={t("home.pillars.overline")}
              title={<>{t("home.pillars.titlePart1")} <br className="hidden lg:block" />{t("home.pillars.titlePart2")}</>}
              introClassName="whitespace-pre-line md:whitespace-normal"
              intro={t("home.pillars.intro")}
            />
          </Reveal>
          <div className="mt-12">
            <ResponsiveCarousel
              items={(() => {
                const loc = t("content.pillars", { returnObjects: true });
                if (!Array.isArray(loc)) return SERVICE_PILLARS;
                return SERVICE_PILLARS.map((it, i) => ({ ...it, ...(loc[i] || {}) }));
              })()}
              testId="pillars-carousel"
              gridCols="md:grid-cols-3"
              renderItem={(item) => <PillarCard item={item} />}
            />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-12 sm:py-16 bg-brand-offwhite" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              overline={t("home.services.overline")}
              title={t("home.services.title")}
              intro={t("home.services.intro")}
              introClassName="whitespace-pre-line md:whitespace-normal"
              className="lg:max-w-4xl"
            />
          </Reveal>
          <div className="mt-12">
            <ResponsiveCarousel
              items={(() => {
                const loc = t("content.services", { returnObjects: true });
                if (!Array.isArray(loc)) return SERVICES;
                return SERVICES.map((it, i) => ({ ...it, ...(loc[i] || {}) }));
              })()}
              testId="services-carousel"
              gridCols="md:grid-cols-2 lg:grid-cols-3"
              renderItem={(item) => <PillarCard item={item} />}
            />
          </div>
        </div>
      </section>

      {/* STATS BANNER */}
      <section className="bg-brand-navy py-12 sm:py-16" data-testid="stats-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white text-center max-w-3xl mx-auto leading-tight">
              {t("home.statsBanner.titleLead")}{" "}
              <br className="hidden lg:block" />
              <span className="text-brand-teal-light">{t("home.statsBanner.titleAccent")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-center text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed mb-12">
              {t("home.statsBanner.introPart1")}{" "}
              <br className="hidden lg:block" />
              {t("home.statsBanner.introPart2")}{" "}
              <span className="text-white font-semibold">{t("home.statsBanner.introYears")}</span>.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-6 max-w-6xl mx-auto">
            {(t("content.stats", { returnObjects: true }) || STATS).map((stat, i) => (
              <Reveal key={stat.label || i} delay={i * 0.08}>
                <div className="text-center">
                  <p className="text-3xl sm:text-4xl font-bold font-heading text-white">
                    <Counter value={stat.value} />
                  </p>
                  <p className="mt-2 text-sm text-white/70 leading-snug">
                    {stat.label}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-12 text-center text-xl sm:text-2xl font-heading text-brand-teal-light font-medium max-w-3xl mx-auto leading-relaxed">
              {t("home.statsBanner.closingPart1")} <span className="text-white font-semibold">{t("home.statsBanner.closingHighlight")}</span>{t("home.statsBanner.closingPart2")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* EXECUTIVE SNAPSHOT (YTD scorecard) */}
      <ExecutiveSnapshot />

      {/* MOBILE UNIT */}
      <section className="py-12 sm:py-16 bg-white" data-testid="mobile-unit-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="relative">
                <div className="rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-brand-navy/10">
                  <img
                    src={IMAGES.mobileUnitSummer}
                    alt="The HepCURE mobile unit at a Hep C testing pop-up"
                    className="w-full h-[300px] sm:h-[420px] object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="hidden sm:block absolute -bottom-10 -right-6 w-44 lg:w-52 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white">
                  <img
                    src={IMAGES.mobileUnitWinter}
                    alt="The HepCURE mobile unit out in the community during winter"
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <Overline>{t("home.mobileUnit.overline")}</Overline>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy">
                {t("home.mobileUnit.title")}
              </h2>
              <p className="mt-5 text-slate-500 leading-relaxed text-justify md:text-left hyphens-auto">
                {t("home.mobileUnit.body1")}
              </p>
              <p className="mt-4 text-slate-500 leading-relaxed whitespace-pre-line md:whitespace-normal">
                {t("home.mobileUnit.body2")}
              </p>
            </Reveal>
          </div>

          {/* Schedule & Locations */}
          <Reveal delay={0.1}>
            <div
              data-testid="mobile-schedule"
              className="mt-14 lg:mt-16 rounded-3xl border border-gray-100 bg-brand-offwhite p-6 sm:p-10 shadow-sm"
            >
              <h3 className="text-2xl sm:text-3xl font-bold font-heading text-brand-navy">
                {t("home.mobileUnit.scheduleTitle")}
              </h3>
              <div className="mt-8 grid md:grid-cols-3 gap-8 md:gap-10">
                <div data-testid="mobile-schedule-schedule">
                  <div className="flex items-center gap-2.5 text-brand-teal">
                    <CalendarDays className="h-5 w-5" strokeWidth={1.8} />
                    <h4 className="text-sm font-bold uppercase tracking-[0.14em]">
                      {t("home.mobileUnit.scheduleLabel")}
                    </h4>
                  </div>
                  <p className="mt-3 text-slate-500 leading-relaxed">
                    {t("home.mobileUnit.scheduleText")}
                  </p>
                </div>

                <div data-testid="mobile-schedule-locations">
                  <div className="flex items-center gap-2.5 text-brand-teal">
                    <MapPin className="h-5 w-5" strokeWidth={1.8} />
                    <h4 className="text-sm font-bold uppercase tracking-[0.14em]">
                      {t("home.mobileUnit.locationsLabel")}
                    </h4>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(t("content.mobileLocations", { returnObjects: true }) || MOBILE_SCHEDULE.locations).map((loc) => (
                      <li
                        key={loc}
                        className="flex items-start gap-2.5 text-slate-500 leading-relaxed"
                      >
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-teal shrink-0" />
                        {loc}
                      </li>
                    ))}
                  </ul>
                </div>

                <div data-testid="mobile-schedule-contact">
                  <div className="flex items-center gap-2.5 text-brand-teal">
                    <Phone className="h-5 w-5" strokeWidth={1.8} />
                    <h4 className="text-sm font-bold uppercase tracking-[0.14em]">
                      {t("home.mobileUnit.contactLabel")}
                    </h4>
                  </div>
                  <p className="mt-3 text-slate-500 leading-relaxed">
                    {t("home.mobileUnit.contactText")}
                  </p>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="mobile-schedule-phone"
                    className="mt-3 inline-flex items-center gap-2 font-semibold text-brand-navy hover:text-brand-teal transition-colors"
                  >
                    {CONTACT.phone}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="py-12 sm:py-16 bg-brand-offwhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <Overline>{t("home.whyHepcure.overline")}</Overline>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy whitespace-pre-line md:whitespace-normal">
                  {t("home.whyHepcure.titleWrap")}
                </h2>
                <p className="mt-5 text-slate-500 leading-relaxed text-justify md:text-left hyphens-auto">
                  {t("home.whyHepcure.body")}
                </p>
                <Link
                  to="/about"
                  data-testid="values-cta"
                  className="mt-8 inline-flex items-center gap-2 text-brand-navy font-semibold hover:text-brand-teal transition-colors"
                >
                  {t("home.whyHepcure.cta")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7 space-y-5">
              {ABOUT_VALUES.map((v, i) => {
                const loc = t("content.values", { returnObjects: true }) || [];
                const item = Array.isArray(loc) && loc[i] ? { ...v, ...loc[i] } : v;
                return (
                <Reveal key={v.icon || i} delay={i * 0.08}>
                  <div
                    data-testid={`value-card-${i}`}
                    className="flex items-start gap-5 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm"
                  >
                    <div className="h-12 w-12 shrink-0 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal">
                      <Icon name={v.icon} className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold font-heading text-brand-navy">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-slate-500 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* OUR REACH — website reach counters (manually updated) */}
      <OurReach />

      {/* PUBLICATIONS / RECOGNITION */}
      <section className="py-12 sm:py-16 bg-brand-offwhite" data-testid="publications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              overline={t("home.publications.overline")}
              className="!max-w-6xl"
              titleClassName="whitespace-pre-line md:whitespace-normal"
              introClassName="text-justify md:text-left hyphens-auto"
              title={t("home.publications.titleWrap")}
              intro={<>{t("home.publications.introPart1")} <br className="hidden lg:block" />{t("home.publications.introPart2")}</>}
            />
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {PUBLICATIONS.map((pub, i) => (
              <Reveal key={pub.url} delay={i * 0.08}>
                <a
                  href={pub.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`publication-${i}`}
                  className="group flex flex-col h-full bg-white rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center rounded-full bg-brand-teal/12 text-brand-teal-dark px-3 py-1 text-xs font-bold uppercase tracking-wider">
                      {pub.venue}
                    </span>
                    <FileText className="h-5 w-5 text-brand-teal" strokeWidth={1.6} />
                  </div>
                  <h3 className="mt-5 flex-1 text-base font-semibold font-heading text-brand-navy leading-snug">
                    {pub.title}
                  </h3>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-navy group-hover:text-brand-teal transition-colors">
                    {t("home.publications.linkLabel")}
                    <ExternalLink className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brand-navy px-8 py-14 sm:px-16 sm:py-20 text-center">
              <div className="absolute -top-10 -right-10 h-56 w-56 rounded-full bg-brand-teal/20 blur-3xl" />
              <div className="relative">
                <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white max-w-2xl mx-auto leading-tight">
                  {t("home.ctaBand.title")}
                </h2>
                <p className="mt-4 text-white/70 max-w-xl mx-auto">
                  {t("home.ctaBand.bodyPart1")}{" "}
                  <br className="hidden lg:block" />
                  {t("home.ctaBand.bodyPart2")}
                </p>
                <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    data-testid="cta-band-contact"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors"
                  >
                    {t("home.ctaBand.ctaPrimary")}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/partners"
                    data-testid="cta-band-partner"
                    className="inline-flex items-center justify-center rounded-full border-2 border-white/40 text-white hover:bg-white hover:text-brand-navy px-8 py-3.5 font-semibold transition-colors"
                  >
                    {t("home.ctaBand.ctaSecondary")}
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FlyerDownload />
    </div>
  );
}
